import { useMemo } from "react";
import {
  AccountDetails,
  InstallationDetails,
} from "../../generated-api/account";
import Loader from "./InstallationDetailTile.loader";
import {
  Wrapper,
  Inner,
  Content,
  Label,
  Value,
  List,
  MapWrapper,
} from "./InstallationDetailTile.styles";
import Map, { MapboxEvent, Marker } from "react-map-gl";
import { useStore } from "../../store/store";
import { useTranslation } from "react-i18next";

interface Props {
  installation?: InstallationDetails;
  account?: AccountDetails;
  isLoading?: boolean;
}

export default function InstallationDetailTile({
  installation,
  account,
  isLoading = true,
}: Props) {
  const { t } = useTranslation();
  const theme = useStore.useTheme();
  const city = useMemo(() => {
    if (!installation?.postalCode && !installation?.city) return "";

    return `${installation?.postalCode ? `${installation?.postalCode} ` : ""}${
      installation?.city ?? ""
    }`;
  }, [installation]);

  const onMapLoad = (mapBoxEvent: MapboxEvent) => {
    setTimeout(() => {
      mapBoxEvent.target.flyTo({
        center: [installation?.longitude ?? 0, installation?.latitude ?? 0],
      });
    }, 1000);
  };

  return isLoading ? (
    <Loader />
  ) : (
    <Wrapper>
      <MapWrapper>
        {installation && (
          <Map
            onLoad={onMapLoad}
            mapboxAccessToken="pk.eyJ1IjoiY2FzYTYxODE3IiwiYSI6ImNsNWpiZTlxZjBkZW4zY3B6MGMzdmhpNmEifQ.NCFLq-nnq9ezUB3trtQCxw"
            mapStyle={
              theme === "light"
                ? "mapbox://styles/mapbox/navigation-day-v1"
                : "mapbox://styles/casa61817/clq2gie1f01rp01qt8uxchir3"
            }
            initialViewState={{
              longitude: installation?.longitude ?? 0,
              latitude: installation?.latitude ?? 0,
              zoom: 11,
            }}
            attributionControl={false}
            reuseMaps
            projection={{ name: "globe" }}
          >
            <Marker
              longitude={installation?.longitude ?? 0}
              latitude={installation?.latitude ?? 0}
              color="red"
              anchor="center"
              style={{ position: "absolute", top: 0, left: 0 }}
            />
          </Map>
        )}
      </MapWrapper>

      <Inner>
        <Content>
          <List>
            <Label>{t("installationDetailTile.accountName")}</Label>
            <Value>{account?.name}</Value>
            <Label>{t("installationDetailTile.accountApplications")}</Label>
            <Value>
              {account?.applications?.join(", ") || "No applications"}
            </Value>
            <Label>{t("installationDetailTile.installationName")}</Label>
            <Value>{installation?.name}</Value>
            {installation?.street && (
              <>
                <Label>{t("installationDetailTile.address")}</Label>
                <Value>{installation?.street}</Value>
              </>
            )}
            {city && (
              <>
                <Label>{t("installationDetailTile.city")}</Label>
                <Value>{city}</Value>
              </>
            )}
            {installation?.state && (
              <>
                <Label>{t("installationDetailTile.state")}</Label>
                <Value>{installation?.state}</Value>
              </>
            )}
            {installation?.country && (
              <>
                <Label>{t("installationDetailTile.country")}</Label>
                <Value>{installation?.country}</Value>
              </>
            )}
          </List>
        </Content>
      </Inner>
    </Wrapper>
  );
}
